import React, { useMemo } from "react";

import {
  FlamebearerCall,
  FlamebearerMetrics
} from "@platformsh/observability-graph/ContinuousProfiling";

import * as Table from "ds/Table";
import useConprofContext from "src/organization/pages/project/environment/observabilitySandbox/experiments/Conprof/hooks/useConprofContext";

import FlamebearerTableBody from "./FlamebearerTableBody";
import FlamebearerTableHead from "./FlamebearerTableHead";
import * as S from "./styles";
import { FunctionSort } from "./types";
import { escapeRegExp } from "./utils";

type Props = {
  filter: string;
  highlightedCall: FlamebearerCall | null;
  onCallHighlighted: (call: FlamebearerCall | null) => void;
};

const FlamebearerTable = ({
  filter,
  highlightedCall,
  onCallHighlighted
}: Props) => {
  const [sort, setSort] = Table.useSort<FunctionSort>({
    key: "exclusive",
    direction: "desc"
  });
  const { data } = useConprofContext();
  const flamebearerData = data?.flamebearerData;

  const sortedCallsMetrics: FlamebearerMetrics[] = useMemo(() => {
    if (!flamebearerData) {
      return [];
    }

    const metrics = [...flamebearerData.flamebearer.callsMetrics];

    switch (sort.key) {
      case "exclusive":
        metrics.sort((a, b) => {
          return a.exclusive.value === b.exclusive.value
            ? a.inclusive.value - b.inclusive.value
            : a.exclusive.value - b.exclusive.value;
        });
        break;
      case "inclusive":
        metrics.sort((a, b) => {
          return a.inclusive.value === b.inclusive.value
            ? a.exclusive.value - b.exclusive.value
            : a.inclusive.value - b.inclusive.value;
        });
        break;
      case "call":
        metrics.sort((a, b) => {
          return a.call.label.localeCompare(b.call.label);
        });
        break;
    }

    if (sort.direction === "desc") {
      return metrics.reverse();
    }
    return metrics;
  }, [flamebearerData, sort.direction, sort.key]);

  const filteredCallsMetrics: FlamebearerMetrics[] = useMemo(() => {
    if (filter === "") {
      return sortedCallsMetrics;
    }

    const search = new RegExp(escapeRegExp(filter), "i");

    return sortedCallsMetrics.filter(({ call: { label } }) =>
      search.test(label)
    );
  }, [filter, sortedCallsMetrics]);

  if (!flamebearerData) {
    return null;
  }

  return (
    <S.Container>
      <Table.Table>
        <FlamebearerTableHead sort={sort} setSort={setSort} />
        <FlamebearerTableBody
          callsMetrics={filteredCallsMetrics}
          highlightedCall={highlightedCall}
          onCallHighlighted={onCallHighlighted}
        />
      </Table.Table>
    </S.Container>
  );
};

export default FlamebearerTable;
